import React, { useState } from 'react';
import { Bell, BellOff, CheckCircle, XCircle, AlertCircle, Loader2 } from 'lucide-react';
import { useNotifications } from '../../contexts/NotificationContext';

const NotificationSettings: React.FC = () => {
  const {
    isSupported,
    permission,
    isSubscribed,
    isLoading,
    error,
    subscribe,
    unsubscribe,
    testNotification,
  } = useNotifications();

  const [showError, setShowError] = useState(false);
  const [testLoading, setTestLoading] = useState(false);

  const handleSubscribe = async () => {
    try {
      setShowError(false);
      await subscribe();
    } catch (error) {
      setShowError(true);
    }
  };

  const handleUnsubscribe = async () => {
    try {
      setShowError(false);
      await unsubscribe();
    } catch (error) {
      setShowError(true);
    }
  };

  const handleTest = async () => {
    try {
      setTestLoading(true);
      setShowError(false);
      await testNotification();
    } catch (error) {
      setShowError(true);
    } finally {
      setTestLoading(false);
    }
  };

  if (!isSupported) {
    return (
      <div className="card p-6">
        <div className="flex items-start space-x-3">
          <div className="bg-yellow-100 rounded-full p-2">
            <AlertCircle className="h-5 w-5 text-yellow-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-secondary-900 mb-2">
              Notificações Push
            </h3>
            <p className="text-sm text-secondary-600">
              As notificações push não são suportadas neste navegador. 
              Tente usar o Chrome, Firefox, ou Edge mais recente.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className={`rounded-full p-2 ${isSubscribed ? 'bg-green-100' : 'bg-secondary-100'}`}>
            {isSubscribed ? (
              <Bell className="h-5 w-5 text-green-600" />
            ) : (
              <BellOff className="h-5 w-5 text-secondary-600" />
            )}
          </div>
          <div>
            <h3 className="text-lg font-semibold text-secondary-900">
              Notificações Push
            </h3>
            <p className="text-sm text-secondary-600">
              {isSubscribed
                ? 'Você receberá notificações quando uma reserva for confirmada'
                : 'Ative para receber notificações de reservas'}
            </p>
          </div>
        </div>
        
        {/* Status Badge */}
        <div className="flex items-center space-x-2">
          {isSubscribed ? (
            <span className="inline-flex items-center space-x-1 rounded-full px-3 py-1 text-xs font-medium bg-green-100 text-green-800">
              <CheckCircle className="h-3 w-3" />
              <span>Ativas</span>
            </span>
          ) : permission === 'denied' ? (
            <span className="inline-flex items-center space-x-1 rounded-full px-3 py-1 text-xs font-medium bg-red-100 text-red-800">
              <XCircle className="h-3 w-3" />
              <span>Bloqueadas</span>
            </span>
          ) : (
            <span className="inline-flex items-center space-x-1 rounded-full px-3 py-1 text-xs font-medium bg-secondary-100 text-secondary-800">
              <AlertCircle className="h-3 w-3" />
              <span>Inativas</span>
            </span>
          )}
        </div>
      </div>

      {/* Error Message */}
      {showError && error && (
        <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-md p-3">
          <div className="flex items-start space-x-2">
            <AlertCircle className="h-4 w-4 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-yellow-700">
              <p className="font-medium mb-1">Notificações Temporariamente Indisponíveis</p>
              <p>{error.includes('not available') || error.includes('server') 
                ? 'O serviço de notificações está em manutenção. Tente novamente mais tarde.' 
                : error}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Permission Denied Help */}
      {permission === 'denied' && (
        <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-md p-3">
          <div className="flex items-start space-x-2">
            <AlertCircle className="h-4 w-4 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-yellow-700">
              <p className="font-medium mb-1">Permissão Negada</p>
              <p>
                Você bloqueou as notificações para este site. Para ativá-las:
              </p>
              <ol className="list-decimal ml-4 mt-2 space-y-1">
                <li>Clique no ícone de cadeado na barra de endereços</li>
                <li>Encontre "Notificações" nas configurações</li>
                <li>Altere para "Permitir"</li>
                <li>Recarregue a página</li>
              </ol>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        {!isSubscribed && permission !== 'denied' && (
          <button
            onClick={handleSubscribe}
            disabled={isLoading}
            className="btn-primary flex items-center space-x-2"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Ativando...</span>
              </>
            ) : (
              <>
                <Bell className="h-4 w-4" />
                <span>Ativar Notificações</span>
              </>
            )}
          </button>
        )}

        {isSubscribed && (
          <>
            <button
              onClick={handleUnsubscribe}
              disabled={isLoading}
              className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded transition-colors disabled:opacity-50 flex items-center space-x-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Desativando...</span>
                </>
              ) : (
                <>
                  <BellOff className="h-4 w-4" />
                  <span>Desativar Notificações</span>
                </>
              )}
            </button>

            <button
              onClick={handleTest}
              disabled={testLoading}
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded transition-colors disabled:opacity-50 flex items-center space-x-2"
            >
              {testLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Enviando...</span>
                </>
              ) : (
                <>
                  <Bell className="h-4 w-4" />
                  <span>Testar Notificação</span>
                </>
              )}
            </button>
          </>
        )}
      </div>

      {/* Information */}
      <div className="mt-4 pt-4 border-t border-secondary-200">
        <h4 className="text-sm font-medium text-secondary-900 mb-2">
          Como funciona?
        </h4>
        <ul className="text-sm text-secondary-600 space-y-1">
          <li>• Receba notificações em tempo real quando uma reserva for confirmada</li>
          <li>• As notificações funcionam mesmo quando o site está fechado</li>
          <li>• Você pode desativar a qualquer momento</li>
          <li>• Seus dados são protegidos e usados apenas para notificações</li>
        </ul>
      </div>
    </div>
  );
};

export default NotificationSettings;

